import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { getMeetingItemByUuid } from 'Shared/selectors/common';
import dompurify from 'dompurify';
import WpLabelled from 'Wpds/components/labelled/WpLabelled';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { WpForm } from 'Wpds/components/form';
import { createServicenowRecord } from 'Shared/actions/todos';
import { servicenowRecordSelectOptions, updateServicenowRecord } from 'Shared/api/Servicenow.api';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpSelect from 'Wpds/components/select/WpSelectV2';
import { VisuallyHidden } from 'Wpds/components/visually-hidden/VisuallyHidden';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  itemUuid: string;
  isOpen: boolean;
  onToggle: () => void;
  existingRecordAttributes?: object;
};

const CopyToServicenowModal: React.FC<Props> = ({ itemUuid, existingRecordAttributes = {}, isOpen, onToggle }) => {
  const dispatch = useDispatch();
  const { tr } = useTranslation();
  const [saving, setSaving] = useState(false);

  // Are we creating a new record or not?
  const newRecord = !existingRecordAttributes?.['value']?.['sys_id'];

  const { body: meetingItemBody } = useDeepEqualSelector(state => getMeetingItemByUuid(itemUuid)(state));
  const meetingItemText = JSON.parse(meetingItemBody)
    .blocks.map(body => body.text)
    .join('\n');

  // Initial values for the form
  const initialValues = newRecord
    ? { sys_class_name: 'rm_story', short_description: meetingItemText }
    : existingRecordAttributes['value'];

  // Which type of SNOW record are we dealing with?
  const [servicenowTablename, setServicenowTableName] = useState(initialValues.sys_class_name);
  const servicenowTablenameOptions = [
    { label: tr('perform.modals.servicenow.story_option'), value: 'rm_story' },
    { label: tr('perform.modals.servicenow.epic_option'), value: 'rm_epic' },
  ];

  // The different reference fields for the SNOW object
  // They correspond to select inputs with dynamically filled options
  const [assignmentGroup, setAssignmentGroup] = useState(initialValues.assignment_group);
  const [assignedTo, setAssignedTo] = useState(initialValues.assigned_to);
  const [sprint, setSprint] = useState(initialValues.sprint);

  // Since the reference fields have dynamic options, we need to fill out an initial set of options for them
  // if we're editing an existing record
  const initialSelectOptions = newRecord
    ? {}
    : Object.fromEntries(
        Object.entries(existingRecordAttributes['value']).map(([k, v]) => [
          k,
          [{ value: v as string, label: existingRecordAttributes['display_value'][k] as string }],
        ])
      );
  // This object stores the options for all the select inputs with dynamic options
  const [selectOptions, setSelectOptions] = useState(initialSelectOptions);
  // This object caches these options for a given set of request parameters do we don't need to reload them every time
  const [selectOptionsCache, setSelectOptionsCache] = useState({});

  // If the assignment group is changed, we have to clear the assigned to and sprint fields
  const handleAssignmentGroupChange = selected => {
    setAssignmentGroup(selected);
    setAssignedTo(null);
    setSprint(null);
  };
  const handleAssignedToChange = selected => {
    setAssignedTo(selected);
  };
  const handleSprintChange = selected => {
    setSprint(selected);
  };
  const handleSelectServicenowTableName = selected => {
    setServicenowTableName(selected);
  };

  // For a given reference field with dynamic select options, this method will set the options for that field
  // these options are dependent on the other field values
  const setSelectOptionsForField = fieldName => {
    const recordAttributes = {
      assignment_group: assignmentGroup,
      assigned_to: assignedTo,
      sprint: sprint,
    };
    const recordAttributesWithoutFieldName = Object.assign({}, recordAttributes);
    delete recordAttributesWithoutFieldName[fieldName];
    const cacheKey = JSON.stringify({ servicenowTablename, recordAttributesWithoutFieldName, fieldName });

    if (selectOptionsCache[cacheKey]) {
      setSelectOptions({ ...selectOptions, [fieldName]: selectOptionsCache[cacheKey] });
      return;
    }
    setSelectOptions({ ...selectOptions, [fieldName]: [{ label: 'Loading...', value: '0', disabled: true }] });
    servicenowRecordSelectOptions(itemUuid, servicenowTablename, recordAttributes, fieldName).then(response => {
      const selectOptionsFromResponse = response.data.length
        ? response.data
        : [{ label: `No options available for ${fieldName} field.`, value: '0', disabled: true }];
      setSelectOptions({ ...selectOptions, [fieldName]: selectOptionsFromResponse });
      setSelectOptionsCache({ ...selectOptionsCache, [cacheKey]: selectOptionsFromResponse });
    });
  };

  const loadAssignmentGroupOptions = () => {
    setSelectOptionsForField('assignment_group');
  };
  const loadAssignedToOptions = () => {
    setSelectOptionsForField('assigned_to');
  };
  const loadSprintOptions = () => {
    setSelectOptionsForField('sprint');
  };

  const btnRef = useRef(null);

  const handleSubmitClick = () => {
    btnRef.current.click();
  };

  const handleSubmit = values => {
    setSaving(true);
    values['assignment_group'] = assignmentGroup || '';
    values['assigned_to'] = assignedTo || '';
    values['sprint'] = sprint || '';
    if (newRecord) {
      dispatch(createServicenowRecord({ itemUuid, tableName: servicenowTablename, recordAttributes: values }))
        .then(() => WpToastManager.success(tr('perform.modals.servicenow.record_created_message')))
        .catch(() => WpToastManager.danger(tr('perform.modals.servicenow.error_message')))
        .finally(() => {
          onToggle();
          setSaving(false);
        });
    } else {
      updateServicenowRecord(servicenowTablename, existingRecordAttributes['value']['sys_id'], values)
        .then(() => WpToastManager.success(tr('perform.modals.servicenow.record_saved_message')))
        .catch(() => WpToastManager.danger(tr('perform.modals.servicenow.error_message')))
        .finally(() => {
          onToggle();
          setSaving(false);
        });
    }
  };

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Copy to Servicenow' });
  }, []);

  return (
    <WpModal size="md" dismissOnBackdrop={false} isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>
        {newRecord
          ? tr('perform.modals.servicenow.copy_title')
          : tr('perform.modals.servicenow.edit_title', existingRecordAttributes['display_value']['sys_class_name'])}
      </WpModalHeader>
      <WpModalBody>
        {newRecord && (
          <>
            <WpSelect
              id="tablename-select"
              label={tr('perform.modals.servicenow.record_to_create_label')}
              options={servicenowTablenameOptions}
              onChange={handleSelectServicenowTableName}
              value={servicenowTablename}
            />
            <br />
          </>
        )}
        {servicenowTablename === 'rm_story' && (
          <>
            <WpSelect
              id="assignment_group"
              label={tr('perform.modals.servicenow.assignment_group_label')}
              options={selectOptions['assignment_group']}
              onChange={handleAssignmentGroupChange}
              value={assignmentGroup}
              onOpen={loadAssignmentGroupOptions}
            />
            <br />
            <WpSelect
              id="assigned_to"
              label={tr('perform.modals.servicenow.assigned_to_label')}
              options={selectOptions['assigned_to']}
              onChange={handleAssignedToChange}
              value={assignedTo}
              onOpen={loadAssignedToOptions}
              forceReset
            />
            <br />
            <WpSelect
              id="sprint"
              label={tr('perform.modals.servicenow.sprint_label')}
              options={selectOptions['sprint']}
              onChange={handleSprintChange}
              value={sprint}
              onOpen={loadSprintOptions}
              disabled={!assignmentGroup}
              forceReset
            />
            <br />
          </>
        )}
        <WpForm onSubmit={handleSubmit}>
          <WpTextField
            id="short_description"
            value={initialValues.short_description}
            label={tr('perform.modals.servicenow.short_description_label')}
            required
          />
          <br />
          <WpTextField
            id="description"
            value={initialValues.description}
            label={tr('perform.modals.servicenow.description_label')}
            multiline
          />
          <br />
          {servicenowTablename === 'rm_story' && newRecord && (
            <>
              <WpTextField
                id="acceptance_criteria"
                value={initialValues.acceptance_criteria}
                label={tr('perform.modals.servicenow.acceptance_criteria_label')}
                multiline
              />
              <br />
            </>
          )}
          {servicenowTablename === 'rm_story' && !newRecord && (
            <>
              <WpLabelled
                label={tr('perform.modals.servicenow.acceptance_criteria_label')}
                id={'acceptance_criteria'}
                showRequiredIndicator={false}
              >
                <span dangerouslySetInnerHTML={{ __html: dompurify.sanitize(initialValues.acceptance_criteria) }} />
              </WpLabelled>
              <br />
            </>
          )}
          <VisuallyHidden>
            <WpButton btnType="submit" ref={btnRef}>
              submit
            </WpButton>
          </VisuallyHidden>
        </WpForm>
      </WpModalBody>

      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" disabled={saving} onClick={onToggle}>
            {tr('perform.common.cancel')}
          </WpButton>
          <WpButton onClick={handleSubmitClick} loading={saving}>
            {newRecord
              ? tr('perform.modals.servicenow.create_record_button')
              : tr('perform.modals.servicenow.save_button')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CopyToServicenowModal;
